import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Whether this person wants to see the machinery.
 *
 * The default interface is for somebody who rented a server and wants a panel
 * on it. They do not need to know that there is a container, a WireGuard
 * interface, a systemd unit, an ACME order or a route table, and a screen that
 * shows them all anyway is a screen they cannot use. Expert mode is how the
 * people who *do* want that ask for it, once, instead of the product guessing.
 *
 * Two things this is not:
 *
 *  - **It is not a permission.** Turning it on reveals controls and
 *    identifiers this account is already entitled to. Technical diagnostics —
 *    command output, paths, the healing engine's reasoning — are withheld by
 *    the server from sessions without `diagnostics.read`, and no switch here
 *    changes that. A client-side gate over data the server already sent hides
 *    it from the person and not from the page.
 *  - **It is not a different product.** Every screen shows the same facts in
 *    both modes. Expert mode adds detail; it never contradicts what the simple
 *    view said, and nothing is only reachable with it on.
 *
 * Stored in `localStorage` because it is a preference of the person at this
 * browser, not of the account — the same customer on a phone is usually not
 * looking for MTU figures.
 */

const STORAGE_KEY = 'paneltunnel.expert';

interface ExpertModeValue {
  readonly expert: boolean;
  readonly setExpert: (value: boolean) => void;
}

const ExpertModeContext = createContext<ExpertModeValue>({
  expert: false,
  setExpert: () => undefined,
});

export function ExpertModeProvider({ children }: { children: ReactNode }): ReactNode {
  const [expert, setExpertState] = useState<boolean>(() => read());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, expert ? '1' : '0');
    } catch {
      // Private browsing, or storage disabled. The preference then lasts as
      // long as the tab, which is a smaller loss than an interface that
      // refuses to render because it could not write a flag.
    }
  }, [expert]);

  const setExpert = useCallback((value: boolean) => setExpertState(value), []);
  const value = useMemo(() => ({ expert, setExpert }), [expert, setExpert]);

  return <ExpertModeContext.Provider value={value}>{children}</ExpertModeContext.Provider>;
}

export function useExpertMode(): ExpertModeValue {
  return useContext(ExpertModeContext);
}

function read(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}
